import { TitularTooltip } from '@campfire/titular-tooltip';
import { DraggableList, DraggableListItem, DraggableListItemHandle } from '@campfire/draggable-list';
import { ButtonBase, Typography, Box, Theme, TextField } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { FieldArrayRenderProps, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { ApplicationQuestionsFormValues, ApplicationQuestionType } from './application-questions-model';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      borderBottom: `1px solid ${theme.color.grey.border}`,
      paddingTop: 8,
      paddingBottom: 8,
      display: 'flex',
      '&:hover': {
        background: theme.palette.grey[100],
      },
      '&:hover $deleteIcon': {
        color: theme.palette.action.disabled,
        '&:hover': {
          color: theme.palette.error.main,
        },
      },
      '&:hover $fieldItemHandle': {
        color: theme.palette.action.active,
      },
    },
    deleteIcon: {
      color: 'transparent',
    },
    fieldItemHandle: {
      paddingLeft: 4,
      paddingRight: 8,
      color: 'transparent',
    },
  })
);

interface ApplicationQuestionListItemProps {
  applicationQuestion: ApplicationQuestionType;
  index: number;
  length: number;
  arrayHelpers: FieldArrayRenderProps;
  onSubmit: () => void;
}

const ApplicationQuestionListItem = React.memo(
  (props: ApplicationQuestionListItemProps) => {
    const { applicationQuestion, index, length, arrayHelpers, onSubmit } = props;
    const [questionToRemove, setQuestionToRemove] = useState<ApplicationQuestionType>();
    const classes = useStyles();

    const name = `applicationQuestions[${index}]`;
    const formik = useFormikContext<ApplicationQuestionsFormValues>();
    const [label, setLabel] = useState(applicationQuestion.label);

    return (
      <>
        <DraggableListItem
          id={`${index}`}
          index={index}
          className={classes.listItem}
          style={index === length - 1 ? { borderBottom: 'none' } : undefined}
        >
          <Box display='flex' flex='1 1 auto' position='relative'>
            <Box>
              <DraggableListItemHandle className={classes.fieldItemHandle} />
            </Box>

            <Box display='flex' flex='1 1 auto' alignContent='center' alignItems='center'>
              <Box flex='1 0 auto'>
                <TextField
                  value={label}
                  onChange={(e) => {
                    setLabel(e.target.value);
                  }}
                  onBlur={() => {
                    if (label === '') {
                      setLabel(applicationQuestion.label);
                      return;
                    }
                    if (label !== applicationQuestion.label) {
                      formik.setFieldValue(name, { ...applicationQuestion, label });
                      onSubmit();
                    }
                  }}
                  multiline
                  fullWidth
                  style={{ width: '100%' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Box>

              <TitularTooltip description='Whether volunteers must answer this question'>
                <Box flex='1 1 auto' maxWidth={112} paddingX={1}>
                  <ButtonBase
                    style={{ fontSize: 13, padding: 4 }}
                    onClick={() => {
                      formik.setFieldValue(name, { ...applicationQuestion, required: !applicationQuestion.required });
                      onSubmit();
                    }}
                  >
                    {applicationQuestion.required ? 'Required' : 'Optional'}
                  </ButtonBase>
                </Box>
              </TitularTooltip>
            </Box>

            <TitularTooltip description='Delete question'>
              <Box>
                <ButtonBase
                  style={{ flex: 1, padding: 8 }}
                  onClick={() => {
                    setQuestionToRemove({ ...applicationQuestion });
                  }}
                >
                  <Close className={classes.deleteIcon} />
                </ButtonBase>
              </Box>
            </TitularTooltip>
          </Box>
        </DraggableListItem>

        {questionToRemove ? (
          <ConfirmationDialog
            title={'Delete Question?'}
            body={
              <>
                <Typography variant='body2' gutterBottom>
                  {'Past applications will not be affected.'}
                </Typography>
              </>
            }
            open
            closeActionText={'Go Back'}
            confirmActionText={'Delete'}
            handleCloseClick={() => setQuestionToRemove(undefined)}
            handleConfirmClick={() => {
              arrayHelpers.remove(index);
              setQuestionToRemove(undefined);
              onSubmit();
            }}
          />
        ) : null}
      </>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.index === nextProps.index && prevProps.applicationQuestion === nextProps.applicationQuestion;
  }
);

interface ApplicationQuestionListFieldSectionProps {
  arrayHelpers: FieldArrayRenderProps;
  onSubmit: () => void;
}

export const ApplicationQuestionListFieldSection = ({
  arrayHelpers,
  onSubmit,
}: ApplicationQuestionListFieldSectionProps) => {
  const formik = useFormikContext<ApplicationQuestionsFormValues>();
  const { applicationQuestions } = formik.values;

  return (
    <>
      <ListFieldHeader />

      <DraggableList
        style={{ paddingTop: 0 }}
        onReorder={(startIdx, endIdx, reorder) => {
          if (formik) {
            formik.setFieldValue('applicationQuestions', reorder(applicationQuestions, startIdx, endIdx));
            onSubmit();
          }
        }}
      >
        {applicationQuestions.map((applicationQuestion, index) => (
          <ApplicationQuestionListItem
            key={applicationQuestion.activityApplicationQuestionId ?? index}
            applicationQuestion={applicationQuestion}
            index={index}
            length={applicationQuestions.length}
            arrayHelpers={arrayHelpers}
            onSubmit={onSubmit}
          />
        ))}
      </DraggableList>
    </>
  );
};

const useHeaderStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerRow: {
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    fieldItemHandle: {
      paddingLeft: 4,
      paddingRight: 8,
      color: 'transparent',
    },
  })
);

const ListFieldHeader = () => {
  const classes = useHeaderStyles();

  return (
    <Box className={classes.headerRow} paddingY={1} display='flex' flex='1 1 auto' position='relative'>
      <Box>
        <DraggableListItemHandle className={classes.fieldItemHandle} />
      </Box>

      <Box display='flex' flex='1 1 auto' alignContent='center' alignItems='center'>
        <Box flex='1 0 auto'>
          <Typography variant='overline' color='textPrimary'>
            {'Question'}
          </Typography>
        </Box>

        <Box flex='1 1 auto' maxWidth={112} paddingX={1}>
          <Typography variant='overline' color='textPrimary'>
            {'Response'}
          </Typography>
        </Box>
      </Box>

      <Box width={40}></Box>
    </Box>
  );
};
